import { Container, GlobalStyles } from '@mui/material';
import { type ReactElement, useEffect, useState } from 'react';
import { useDragDropManager } from 'react-dnd';
import { matchPath, Outlet, useLocation, useNavigation } from 'react-router';

import useHandleUserChange from '@@auth/hooks/useHandleUserChange';
import parseJWT from '@@auth/utils/parseJWT';
import Loader from '@@components/Loader';
import MenuBar from '@@components/MenuBar';
import useLastRouteTracker from '@@containers/LastRouteTracker/useLastRouteTracker';
import Navigation from '@@containers/Navigation/Navigation';
import { useContentLocale, useSettingsStore } from '@@scripts/store/settings/useSettingsStore';
import { useAuthStore, useIsAuthenticated } from '@@scripts/store/useAuthStore';
import { useDevToolbarMode } from '@@scripts/store/useDevToolbarStore';
import CycleRegionShortcut from '@@shortcuts/components/CycleRegionShortcut';
import ShortcutsDialog from '@@shortcuts/components/ShortcutsDialog';
import { addDataDogRUMAction, setDataDogRUMUserId } from '@@utils/dataDog';

import AppNotifications from './AppNotifications';
import PageLayout, {
    PageLayoutContent,
    PageLayoutFooter,
    PageLayoutHeader,
    PageLayoutNavigation,
    PageLayoutNotificationBar,
    PageLayoutSidebar,
    PageLayoutToolbar,
} from './PageLayout/PageLayout';
import PageTitle from './PageTitle';
import DeveloperToolbar from '../DeveloperToolbar/DeveloperToolbar';

const LOADER_THRESHOLD = 500;

const VITE_PRELOAD_ERROR_RELOAD_KEY = 'vite_preload_error_reloaded';

type SlowLoaderProps = {
    content: ReactElement;
};

const SlowLoader = (props: SlowLoaderProps) => {
    const { content } = props;

    const [showLoader, setShowLoader] = useState(false);
    const navigation = useNavigation();

    useEffect(() => {
        if (navigation.state !== 'loading') {
            setShowLoader(false);
        } else {
            const timer = setTimeout(() => {
                setShowLoader(true);
            }, LOADER_THRESHOLD);

            return () => {
                clearTimeout(timer);
            };
        }
    }, [navigation.state]);

    return navigation.state === 'loading' && showLoader ? <Loader /> : content;
};

// Important note: It is important to render global styles globally. Rendering them in a nested component, can lead to
// performance issues! This is what we've experienced for the insert toolbar of the richt text editor, which became
// a major incident!
// https://tamedia.atlassian.net/browse/CD2-8379
const StyledDndGlobalStyles = `
    body.dragging {
        // When a user drags an item to the bottom of the browser window, the default behaviour is to scroll downwards on the
        // page (which makes sense, in order to drop the dragged item somewhere further down on the page).
        // MenuBar prevented the user from doing so, because it kind of blocked the drag event from being propagated to
        // the scrollable area. In order to not block this, we disable 'pointer-events' while the user is dragging. This
        // fixes the problem.
        // Sidenote: 'useDragBlock.ts' adds the 'dragging' class to the body when a user starts to drag a rich text editor
        // element.
        ${MenuBar} {
            pointer-events: none;
        }
    }
`;

const DndGlobalStyles = () => {
    const dndManager = useDragDropManager();

    useEffect(() => {
        const unsubscribe = dndManager.getMonitor().subscribeToStateChange(() => {
            if (dndManager.getMonitor().isDragging()) {
                document.body.classList.add('dragging');
            } else {
                document.body.classList.remove('dragging');
            }
        });

        return () => {
            unsubscribe();
        };
    }, []);

    return <GlobalStyles styles={StyledDndGlobalStyles} />;
};

export const pathsWithoutPageLayout = ['/isolated/*', '/plate'];

export const pathsWithoutNavigation = ['/preview'];

// @ts-expect-error TS7006 Issue #4120
const matchesOneOfPaths = (paths, path) =>
    // this workaround is needed as path="/" which wraps every sibling in the navigation
    // also enables it for /embed as it matches "/".
    // an exact match for "/" breaks the navigation if a child route is rendered.
    // @ts-expect-error TS7006 Issue #4120
    paths.some((pathname) =>
        matchPath(
            {
                path: pathname,
                end: false,
                caseSensitive: false,
            },
            path,
        ),
    );

const AppWrapper = () => {
    const location = useLocation();

    useLastRouteTracker();

    // Vite fires this event when a dynamic import (lazy route) fails - typically because a new
    // deployment replaced the hashed chunk filenames. Reloading fetches the new index.html and
    // resolves the missing chunks. The sessionStorage key stores the build VERSION to allow one
    // reload per deployment while preventing infinite reload loops.
    useEffect(() => {
        const handleVitePreloadError = () => {
            const currentVersion = VERSION ?? BUILD_TIMESTAMP ?? 'unknown';
            const reloadedVersion = sessionStorage.getItem(VITE_PRELOAD_ERROR_RELOAD_KEY);

            if (reloadedVersion === currentVersion) {
                // Already reloaded once for this deployment - don't loop. Let the error surface naturally.
                return;
            }

            sessionStorage.setItem(VITE_PRELOAD_ERROR_RELOAD_KEY, currentVersion);
            window.location.reload();
        };

        window.addEventListener('vite:preloadError', handleVitePreloadError);

        return () => {
            window.removeEventListener('vite:preloadError', handleVitePreloadError);
        };
    }, []);

    const { fetchPreferences, dispatchPreferences } = useHandleUserChange();
    // Do NOT get data directly from JWT, because if the same user has opened 2 tabs, and in window 1
    // he is changing for example the ui locale, then when refreshing tab 2, he must get the new lang. if we
    // would take JWT data, the new lang would only be applied after the next login
    const isAuthenticated = useIsAuthenticated();
    const contentLocale = useContentLocale();
    const devToolbar = useDevToolbarMode();
    const userId = useAuthStore((state) => state.userId);
    const accessToken = useAuthStore((state) => state.accessToken);

    const setPreferencesLoaded = useSettingsStore((state) => state.setPreferencesLoaded);

    const content = isAuthenticated && !contentLocale ? <div /> : <Outlet />;

    // In order to avoid using acceptance environments when not necessary
    // We revert to default ones when token expires
    useEffect(() => {
        if (accessToken && !isAuthenticated) {
            localStorage.removeItem('CDII_API');
        }
    }, [isAuthenticated]);

    useEffect(() => {
        if (isAuthenticated && userId) {
            setPreferencesLoaded(false);

            fetchPreferences()
                .then(({ body }) => {
                    dispatchPreferences(body);
                })
                .finally(() => setPreferencesLoaded(true));

            if (accessToken) {
                const jwt = parseJWT(accessToken);

                const username = jwt && jwt.user_name ? jwt.user_name : 'unknown';

                setDataDogRUMUserId(username);
            }
        }
    }, [isAuthenticated, userId, accessToken]);

    useEffect(() => {
        const reducedMotionEnabled = window.matchMedia?.(
            '(prefers-reduced-motion: reduce)',
        ).matches;

        if (reducedMotionEnabled) {
            addDataDogRUMAction('reducedMotionInfo', { reducedMotionEnabled });
        }
    }, []);

    const isPathWithoutPageLayout = matchesOneOfPaths(pathsWithoutPageLayout, location.pathname);
    const isPathWithoutNavigation = matchesOneOfPaths(pathsWithoutNavigation, location.pathname);

    return (
        <Container disableGutters fixed sx={{ display: 'flex', minHeight: '100%' }}>
            <DndGlobalStyles />

            <PageTitle />

            {isAuthenticated && !isPathWithoutPageLayout ? (
                <>
                    <PageLayout>
                        <PageLayoutNavigation as="nav">
                            {!isPathWithoutNavigation && <Navigation />}
                        </PageLayoutNavigation>

                        <PageLayoutNotificationBar>
                            <AppNotifications />
                        </PageLayoutNotificationBar>

                        <PageLayoutHeader />

                        <PageLayoutSidebar as="aside" />

                        <PageLayoutContent as="main">
                            <SlowLoader content={content} />
                        </PageLayoutContent>

                        <PageLayoutToolbar />

                        <PageLayoutFooter />
                    </PageLayout>

                    {devToolbar && <DeveloperToolbar />}

                    <ShortcutsDialog />
                    <CycleRegionShortcut />
                </>
            ) : (
                content
            )}
        </Container>
    );
};

export default AppWrapper;
